import jsPDF from 'jspdf';
import Logo from "../images/hotel_logo.png";
import { HOTEL_NAME, HOTEL_ADDRESS, HOTEL_PHONE, HOTEL_EMAIL } from "../Variables.js";

// Helper: Format date as "15 Dec 2025"
const formatDateDDMMMYYYY = (dateInput) => {
  if (!dateInput) return 'N/A';
  const date = dateInput.toDate ? dateInput.toDate() : new Date(dateInput);
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  }).replace(',', '');
};

const generatePaymentReceiptPDF = (payment, booking, cashierName, incidentalTotal = 0) => {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: [80, 297] // 80mm thermal receipt size
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 5;
  const contentWidth = pageWidth - (2 * margin);
  let yPos = margin;

  // === Logo (Optional) ===
  try {
    const logoWidth = 15;
    const logoHeight = 15;
    const logoX = (pageWidth - logoWidth) / 2;
    doc.addImage(Logo, 'PNG', logoX, yPos, logoWidth, logoHeight);
    yPos += logoHeight + 3;
  } catch (err) {
    console.log("Logo not found, skipping...");
  }

  // === Hotel Name ===
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  const hotelNameLines = doc.splitTextToSize(HOTEL_NAME.toUpperCase(), contentWidth);
  hotelNameLines.forEach((line) => {
    doc.text(line, pageWidth / 2, yPos, { align: 'center' });
    yPos += 5;
  });

  // === Hotel Contact Info ===
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);

  const addressLines = doc.splitTextToSize(HOTEL_ADDRESS, contentWidth);
  addressLines.forEach((line) => {
    doc.text(line, pageWidth / 2, yPos, { align: 'center' });
    yPos += 4;
  });

  doc.text(`Tel: ${HOTEL_PHONE}`, pageWidth / 2, yPos, { align: 'center' });
  yPos += 4;
  doc.text(`Email: ${HOTEL_EMAIL}`, pageWidth / 2, yPos, { align: 'center' });
  yPos += 8;

  // === Receipt Header ===
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('PAYMENT RECEIPT', pageWidth / 2, yPos, { align: 'center' });
  yPos += 3;

  // === Divider Line ===
  doc.setLineWidth(0.2);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 6;

  // === Receipt Details ===
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');

  const receiptNo = Math.floor(Math.random() * 1000000).toString().padStart(6, '0');

  // Calculate remaining balance after this payment
  const previousDeposit = (booking.deposit || 0) - payment.amount;
  const remainingBalance = (booking.totalPrice || 0) + incidentalTotal - (previousDeposit + payment.amount);

  const details = [
    `Date: ${formatDateDDMMMYYYY(new Date())}`,
    `Receipt No: ${receiptNo}`,
    `Cashier: ${cashierName || 'Staff'}`,
    `Guest: ${booking.guestName || 'N/A'}`,
    `Room: ${booking.roomNumber || 'N/A'}`,
    `Check-in: ${formatDateDDMMMYYYY(booking.checkInDate)}`,
    `Check-out: ${formatDateDDMMMYYYY(booking.checkOutDate)}`,
    `Payer: ${payment.payerName || 'N/A'}`,
    `Payment Method: ${payment.paymentMethod || 'Transfer'}`,
    `Amount Paid: N${payment.amount.toLocaleString()}`,
    `Balance After Payment: N${remainingBalance.toLocaleString()}`,
    `Payment Date: ${formatDateDDMMMYYYY(payment.timestamp)}`
  ];

  if (payment.notes) {
    details.push(`Notes: ${payment.notes}`);
  }

  details.forEach(text => {
    const wrapped = doc.splitTextToSize(text, contentWidth);
    wrapped.forEach(line => {
      doc.text(line, margin, yPos);
      yPos += 5;
    });
  });

  // === Total Paid Section ===
  yPos += 5;
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 5;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('TOTAL PAID:', margin, yPos);
  doc.text(`N${payment.amount.toLocaleString()}`, margin + 35, yPos);
  yPos += 10;

  // === Footer ===
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(9);
  doc.text('Thank you for staying with us!', pageWidth / 2, yPos, { align: 'center' });
  yPos += 6;

  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  doc.text(`Printed on ${new Date().toLocaleString()}`, pageWidth / 2, yPos, { align: 'center' });

  // Auto-extend page if content is long
  const contentHeight = yPos + 10;
  if (contentHeight > 297) {
    doc.internal.pageSize.height = contentHeight;
  }

  // === Save PDF ===
  const safeGuestName = (booking.guestName || 'Guest').replace(/[^a-zA-Z0-9]/g, '_');
  doc.save(`Receipt_${safeGuestName}_${receiptNo}.pdf`);
};

export default generatePaymentReceiptPDF;